import {
  NestedValues,
  OfflinePercentageSplit,
  OfflineProduct,
  OfflineProductPost
} from '@interfaces/OfflineProductData';
import { CompanyDataDrawerForm } from '../../components/page-components/company-contact-broker-details/CompanyData';
import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';
import {
  CompanyInterface,
  CompanySummaryInterface,
  CompanySubscriptionInterface,
  GetAllCompanyData,
  GetCompanyDetails,
  CommunicationDetails,
  CompanyBrandingPost,
  CompanyDetailsPOST,
  CompanyOpportunityUpdatePayload,
  PostEmail,
  TenantEmailProviderConfiguration,
  TenantEmailProviderConfigurationPost
} from '@interfaces/Company.interface';
import {
  BankAccountDetails,
  BankAccountDetailsSecure,
  BankAccountVerified
} from '@interfaces/BankAccountDetails.interface';
import { ComplianceInformationInterface } from '@interfaces/ComplianceInformation.interface';
import { TaskInterface } from '@interfaces/Task.interface';
import type { CompanyLookupResult } from './company-lookup';
import { buildCompanyBrandingFormData } from './branding-payload';

export type CompanySearchResult = {
  id: number;
  name: string;
};

export class ApiCompany extends BaseApiClient {
  /**
   * Get list of items
   */
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<CompanyInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/company', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get an item
   */
  async get(id: number): Promise<CompanyInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/company/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getSummary(id: number | string): Promise<CompanySummaryInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/companies/${id}/summary`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getCompanyDetails(): Promise<GetCompanyDetails> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/company/details')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getCompany(): Promise<GetCompanyDetails[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/company')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getAllCompanyData(
    companyId: number | string | undefined
  ): Promise<GetAllCompanyData> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/companies/${companyId}`)
        .then((res) => {
          if (res) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getCompanyOverview(
    companyId: number | string | undefined
  ): Promise<GetAllCompanyData> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/companies/${companyId}/overview`)
        .then((res) => {
          if (res) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getCompaniesOrbis(
    searchTerm: string,
    country = 'GB'
  ): Promise<CompanyLookupResult[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/companies/orbis?filter[search]=${encodeURIComponent(
            searchTerm
          )}&filter[country]=${encodeURIComponent(country)}`
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async searchExistingCompanies(
    searchTerm: string,
    pageSize = 10,
    type?: string
  ): Promise<CompanySearchResult[]> {
    return new Promise((resolve, reject) => {
      const typeFilter = type
        ? `&filter[type]=${encodeURIComponent(type)}`
        : '';
      this.client
        .get(
          `/companies/search?filter[name]=${encodeURIComponent(
            searchTerm
          )}&page[size]=${pageSize}${typeFilter}`
        )
        .then((res) => {
          if (res.data?.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async postCompaniesOrbis(dto?: {
    orbis_id?: string | null;
    match_token?: string | null;
    company_number?: string | null;
  }): Promise<CompanyInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/companies/orbis', dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getBankDetails(
    companyId: number | string | undefined
  ): Promise<BankAccountDetailsSecure> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/company/${companyId}/bank-account-details`)
        .then((res) => {
          if (res) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getCompanySubscription(): Promise<CompanySubscriptionInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/company/subscription')
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async verifyBankAccount(
    params: BankAccountDetails
  ): Promise<BankAccountVerified> {
    return new Promise((resolve, reject) => {
      const queryParams = new URLSearchParams({
        account_name: params.account_name || '',
        account_number: params.account_number?.toString() || '',
        sort_code: params.sort_code || ''
      }).toString();

      this.client
        .getList(`/company/validate-bank-account-details?${queryParams}`)
        .then((res) => {
          if (res) {
            resolve({ is_verified: res.data });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Assign an individual to a company
   */
  async storeBankAccountDetails(
    companyId: number | undefined,
    dto?: BankAccountDetails
  ) {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/company/${companyId}/bank-account-details`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Store a company
   */
  async store(dto: {
    name: string;
    address: string | null;
    employee_size_id: number | null;
    website: string | null;
    industry_id: number | null;
    industry_type?: string | null;
    est_annual_gwp: number | null;
    source_of_contact?: string | null;
    description: string | null;
    type_ids: number[] | null;
    is_savings_introducer?: boolean;
  }): Promise<CompanyInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/company', dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateBrokerData(
    companyId: string | undefined,
    brokerData: CompanyDataDrawerForm
  ) {
    if (!companyId) {
      throw new Error('Company ID is required');
    }
    return new Promise((resolve, reject) => {
      this.client
        .post(`/company/${companyId}/broker-data`, brokerData)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Assign an individual to a company
   */
  async assignIndividual(
    companyId: number,
    individualId: number,
    dto?: {
      job_title_id?: number | null;
      job_title?: string | null;
      is_primary?: boolean | null;
    }
  ) {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/company/${companyId}/individual`, {
          company_id: companyId,
          individual_id: individualId,
          ...dto
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getTenantParent(): Promise<GetCompanyDetails> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/company/tenant-parent')
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  // companies page
  /**
   * Store a company
   */
  async storeCompany(dto: CompanyDetailsPOST): Promise<GetAllCompanyData> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/company', dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateCompany(
    companyId: number | string | undefined,
    dto: CompanyDetailsPOST
  ): Promise<GetAllCompanyData> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/company/${companyId}`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateCompanyOpportunity(
    companyId: number | string | undefined,
    dto: CompanyOpportunityUpdatePayload
  ): Promise<GetAllCompanyData> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/company/${companyId}/opportunity`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async dataLists(
    companyId: string | undefined,
    listData: ListDataParams,
    line?: number | string, // 1 - 2 are for personal & commercial
    searchTerm?: string
  ): Promise<ListDataResponse<OfflineProduct>> {
    return await new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList(
          `/companies/${companyId}/lines?filter[type]=${line}&filter[include-unselected]=true`,
          listData
        )
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  getCompanyLinesSplit(
    companyId: string | undefined
  ): Promise<OfflinePercentageSplit> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/companies/${companyId}/lines-split`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async storeCommunicationDetails(
    companyId: number | undefined,
    dto: CommunicationDetails
  ): Promise<GetAllCompanyData> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/company/${companyId}/communication-details`, dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async storeAddProduct(
    id: string | number | undefined,
    payload: OfflineProductPost
  ) {
    return new Promise((resolve, reject) => {
      this.client
        .post(`company-line/${id}/broker-data`, payload)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async storeCompanyBranding(
    companyId: string | number | undefined,
    dto: CompanyBrandingPost
  ): Promise<GetAllCompanyData> {
    return new Promise((resolve, reject) => {
      this.client
        .post(
          `/company/${companyId}/branding`,
          buildCompanyBrandingFormData(dto),
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async storePercentageSplit(
    id: string | undefined,
    payload: OfflinePercentageSplit
  ) {
    return new Promise((resolve, reject) => {
      this.client
        .post(`company/${id}/broker-data`, payload)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async getTenantEmailProvider(): Promise<TenantEmailProviderConfiguration | null> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/tenant/email-provider')
        .then((res) => resolve(res.data ?? null))
        .catch((err) => {
          if (err?.response?.status === 404) {
            resolve(null);
            return;
          }
          reject(err);
        });
    });
  }

  async storeTenantEmailProvider(
    payload: TenantEmailProviderConfigurationPost
  ): Promise<TenantEmailProviderConfiguration> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/tenant/email-provider', payload)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateTenantEmailProvider(
    payload: TenantEmailProviderConfigurationPost
  ): Promise<TenantEmailProviderConfiguration> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/tenant/email-provider', payload)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async deleteTenantEmailProvider(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete('/tenant/email-provider')
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  async testTenantEmailProvider(): Promise<{ success: boolean; message?: string }> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/tenant/email-provider/test')
        .then((res) => resolve(res.data ?? { success: true }))
        .catch((err) => reject(err));
    });
  }

  async deleteProduct(companyid: string | undefined, rowid: number) {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`companies/${companyid}/lines/${rowid}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  prefillLines(): Promise<
    {
      id: number;
      name: string;
      options: NestedValues[];
    }[]
  > {
    return new Promise((resolve, reject) => {
      this.client
        .get('/data-lists?filter[group]=product')
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async storeGetCompanyId(
    companyId: string | undefined,
    companyLineId: number
  ): Promise<{ id: string }> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/companies/${companyId}/lines/${companyLineId}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async sendEmail(dto: PostEmail) {
    return this.client.post('company/email', dto);
  }

  /**
   * Get compliance information
   */
  async getComplianceInformation(
    id: string | undefined
  ): Promise<ComplianceInformationInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/company/${id}/compliance-information`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async generateComplianceInformation(
    id: string | undefined
  ): Promise<ComplianceInformationInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/company/${id}/compliance-information/generate`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get company tasks by endpoint
   */
  async getCompanyTasksByEndpoint(
    companyId: string | number,
    endpoint: string
  ): Promise<TaskInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/companies/${companyId}/${endpoint}?include=team,teamMember,teamMember.teams,priority,pipeline,pipelineStage,datapoints,sourceIndividual,paymentFrequency,taskProducts`
        )
        .then((res) => {
          if (res.data && res.data.data) {
            resolve(res.data.data);
          } else if (res.data && Array.isArray(res.data)) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get company won tasks
   */
  async getCompanyWonTasks(
    companyId: string | number
  ): Promise<TaskInterface[]> {
    return this.getCompanyTasksByEndpoint(companyId, 'tasks/won');
  }

  /**
   * Get company active tasks (tasks within pipelines)
   */
  async getCompanyActiveTasks(
    companyId: string | number
  ): Promise<TaskInterface[]> {
    return this.getCompanyTasksByEndpoint(companyId, 'tasks/active');
  }

  /**
   * Get company growth opportunities
   */
  async getCompanyOpportunities(
    companyId: string | number
  ): Promise<TaskInterface[]> {
    return this.getCompanyTasksByEndpoint(companyId, 'opportunities');
  }
}

export { companyApiQueryKeys } from './query-keys';
export {
  useCompanyApiActiveTasksQuery,
  useCompanyApiAllDataQuery,
  useCompanyApiBankDetailsQuery,
  useCompanyApiCompanyDetailsQuery,
  useCompanyApiCompanyRootQuery,
  useCompanyApiComplianceQuery,
  useCompanyApiDataListsQuery,
  useCompanyApiGetQuery,
  useCompanyApiLinesSplitQuery,
  useCompanyApiListQuery,
  useCompanyApiOpportunitiesQuery,
  useCompanyApiOrbisSearchQuery,
  useCompanyApiOverviewQuery,
  useCompanyApiPrefillLinesQuery,
  useCompanyApiSubscriptionQuery,
  useCompanyApiTasksByEndpointQuery,
  useCompanyApiTenantParentQuery,
  useCompanyApiVerifyBankAccountQuery,
  useCompanyApiWonTasksQuery
} from './use-company-api-queries';
export {
  useCompanyApiAssignIndividualMutation,
  useCompanyApiDeleteProductMutation,
  useCompanyApiGenerateComplianceMutation,
  useCompanyApiImapAuthAttemptMutation,
  useCompanyApiPostOrbisMutation,
  useCompanyApiSendEmailMutation,
  useCompanyApiStoreAddProductMutation,
  useCompanyApiStoreBankAccountMutation,
  useCompanyApiStoreBrandingMutation,
  useCompanyApiStoreCommunicationDetailsMutation,
  useCompanyApiStoreCompanyMutation,
  useCompanyApiStoreGetCompanyIdMutation,
  useCompanyApiStoreImapMutation,
  useCompanyApiStoreMutation,
  useCompanyApiStorePercentageSplitMutation,
  useCompanyApiUpdateBrokerDataMutation,
  useCompanyApiUpdateCompanyMutation
} from './use-company-api-mutations';
